import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatFCFA, formatDate } from "@/lib/format";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Home, History as HistoryIcon } from "lucide-react";
import SearchInput from "@/components/SearchInput";
import Pagination, { paginate } from "@/components/Pagination";
import { toast } from "sonner";

const empty = { nom: "", proprietaire_id: "", adresse: "", type: "Maison (Cour commune)", nb_pieces: 0, loyer_mensuel: 0, statut: "disponible", description: "", image_url: "", architectures: [], contract_template_id: "" };

const TYPES = [
  "Maison (Cour commune)",
  "Maison (Cour unique)",
  "Appartement meublée",
  "Villa meublé",
  "Magasin",
  "Autre",
];
const STATUTS = [
  { v: "disponible", label: "Disponible", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { v: "occupee", label: "Occupée", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  { v: "maintenance", label: "Maintenance", cls: "bg-amber-100 text-amber-800 border-amber-200" },
];

const emptyArchi = { type: "", nb_plans: 1, prix: 0 };

export default function Maisons() {
  const [items, setItems] = useState([]);
  const [proprios, setProprios] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [histOpen, setHistOpen] = useState(false);
  const [histData, setHistData] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const load = async () => {
    const [m, p, t] = await Promise.all([
      api.get("/maisons"),
      api.get("/proprietaires"),
      api.get("/contract-templates").catch(() => ({ data: [] })),
    ]);
    setItems(m.data); setProprios(p.data); setTemplates(t.data || []);
  };
  useEffect(() => { load(); }, []);

  const proprioName = (id) => proprios.find((x) => x.id === id)?.nom || "—";

  const filteredItems = items.filter((m) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (m.nom || "").toLowerCase().includes(q) ||
      (m.adresse || "").toLowerCase().includes(q) ||
      (m.type || "").toLowerCase().includes(q) ||
      (proprioName(m.proprietaire_id) || "").toLowerCase().includes(q)
    );
  });
  const pag = paginate(filteredItems, page);
  useEffect(() => { setPage(1); }, [search]);

  const openHistory = async (maison) => {
    setHistOpen(true);
    setHistLoading(true);
    setHistData({ maison_nom: maison.nom, plans: [] });
    try {
      const { data } = await api.get(`/maisons/${maison.id}/occupation-history`);
      setHistData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
      setHistOpen(false);
    } finally { setHistLoading(false); }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const archis = (form.architectures || [])
        .filter((a) => (a.type || "").trim() !== "")
        .map((a) => ({
          type: String(a.type).trim(),
          nb_plans: Number(a.nb_plans || 0),
          prix: Number(a.prix || 0),
        }));
      // Derive aggregated values from architectures for back-compat
      const prices = archis.map((a) => a.prix).filter((p) => p > 0);
      const totalPlans = archis.reduce((s, a) => s + (a.nb_plans || 0), 0);
      const derivedLoyer = prices.length ? Math.min(...prices) : 0;
      const payload = {
        ...form,
        nb_pieces: totalPlans,
        loyer_mensuel: derivedLoyer,
        architectures: archis,
        contract_template_id: form.contract_template_id || null,
      };
      if (editing) {
        await api.put(`/maisons/${editing}`, payload);
        toast.success("Maison modifiée");
      } else {
        await api.post("/maisons", payload);
        toast.success("Maison créée");
      }
      setOpen(false); setForm(empty); setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    }
  };

  const onEdit = (it) => { setEditing(it.id); setForm({ ...empty, ...it, architectures: Array.isArray(it.architectures) ? it.architectures : [], contract_template_id: it.contract_template_id || "" }); setOpen(true); };
  const onDelete = async () => {
    try { await api.delete(`/maisons/${confirmId}`); toast.success("Supprimée"); setConfirmId(null); load(); }
    catch (e) { toast.error("Suppression échouée"); }
  };

  return (
    <div>
      <PageHeader
        title="Maisons"
        subtitle="Catalogue de vos biens immobiliers, avec leur statut d'occupation et loyer mensuel."
        testid="page-maisons"
        accent="indigo"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <SearchInput value={search} onChange={setSearch} placeholder="Rechercher maison…" testid="search-maisons" />
            <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-orange-500 hover:bg-orange-600 text-white shadow-md shadow-orange-900/20" data-testid="btn-add-maison">
              <Plus className="w-4 h-4 mr-2" /> Nouvelle maison
            </Button>
          </div>
        }
      />

      <Card className="rounded-xl border border-slate-200 shadow-sm overflow-hidden bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Nom</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Propriétaire</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Type</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Adresse</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Architectures</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Occupation</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">À partir de</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Statut</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-12">
                <Home className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                {search ? `Aucun résultat pour « ${search} ».` : "Aucune maison enregistrée."}
              </TableCell></TableRow>
            )}
            {pag.rows.map((m) => {
              const s = STATUTS.find((x) => x.v === m.statut);
              const archis = Array.isArray(m.architectures) ? m.architectures : [];
              const prices = archis.map((a) => a.prix).filter((p) => p > 0);
              const minPrice = prices.length ? Math.min(...prices) : (m.loyer_mensuel || 0);
              const occ = m.occupation || { total: 0, occupied: 0 };
              const ratio = occ.total ? Math.round((occ.occupied / occ.total) * 100) : 0;
              return (
                <TableRow key={m.id} className="hover:bg-slate-50/60">
                  <TableCell className="font-medium text-slate-900">{m.nom}</TableCell>
                  <TableCell className="text-slate-600">{proprioName(m.proprietaire_id)}</TableCell>
                  <TableCell className="text-slate-600">{m.type}</TableCell>
                  <TableCell className="text-slate-600">{m.adresse || "—"}</TableCell>
                  <TableCell>
                    {archis.length === 0 ? (
                      <span className="text-slate-400 text-sm">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {archis.map((a, i) => (
                          <Badge key={i} variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-50 font-normal">
                            {a.type} · {a.nb_plans}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell data-testid={`occupation-${m.id}`}>
                    {occ.total ? (
                      <div className="space-y-1 min-w-[100px]">
                        <div className="text-sm font-medium text-slate-900">
                          <span className={occ.occupied === occ.total ? "text-rose-700" : "text-emerald-700"}>{occ.occupied}</span>
                          <span className="text-slate-400">/</span>
                          <span>{occ.total}</span>
                          <span className="text-xs text-slate-500 ml-1">&nbsp;occupés</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className={`h-full ${ratio >= 100 ? "bg-rose-500" : ratio >= 70 ? "bg-amber-500" : "bg-emerald-500"} transition-all`}
                            style={{ width: `${ratio}%` }}
                          />
                        </div>
                      </div>
                    ) : (
                      <span className="text-slate-400 text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-medium text-slate-900">
                    {minPrice ? formatFCFA(minPrice) : <span className="text-slate-400">—</span>}
                  </TableCell>
                  <TableCell><Badge className={`${s?.cls} border hover:opacity-90`}>{s?.label}</Badge></TableCell>
                  <TableCell className="text-right">
                    {occ.total > 0 && (
                      <Button size="sm" variant="ghost" onClick={() => openHistory(m)} title="Historique d'occupation" data-testid={`btn-history-${m.id}`}>
                        <HistoryIcon className="w-4 h-4 text-indigo-700" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => onEdit(m)} data-testid={`btn-edit-maison-${m.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmId(m.id)} data-testid={`btn-delete-maison-${m.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
      <Pagination {...pag} onChange={setPage} testid="pagination-maisons" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="dialog-maison">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Modifier maison" : "Nouvelle maison"}</DialogTitle></DialogHeader>
          <form onSubmit={onSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 col-span-2">
                <Label>Nom *</Label>
                <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} required data-testid="input-maison-nom" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Propriétaire *</Label>
                <Select value={form.proprietaire_id} onValueChange={(v) => setForm({ ...form, proprietaire_id: v })}>
                  <SelectTrigger data-testid="select-maison-proprio"><SelectValue placeholder="Choisir un propriétaire" /></SelectTrigger>
                  <SelectContent>
                    {proprios.map((p) => <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="select-maison-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select value={form.statut} onValueChange={(v) => setForm({ ...form, statut: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Modèle de contrat de bail</Label>
                <Select
                  value={form.contract_template_id || "_default"}
                  onValueChange={(v) => setForm({ ...form, contract_template_id: v === "_default" ? "" : v })}
                >
                  <SelectTrigger data-testid="select-maison-template">
                    <SelectValue placeholder="Modèle par défaut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_default">Modèle par défaut (Configuration)</SelectItem>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Vous pouvez créer plusieurs modèles dans <span className="font-medium">Configuration → Contrat de bail</span> et en attribuer un par maison.
                </p>
              </div>
            </div>

            {/* Architectures */}
            <div className="rounded-lg border border-emerald-200/70 bg-emerald-50/40 p-4" data-testid="archi-section">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] font-bold text-emerald-700/80">Architecture</div>
                  <div className="font-heading text-base font-semibold text-slate-900">Plans disponibles</div>
                  <p className="text-xs text-slate-500 mt-0.5">Type d'architecture, nombre de plans disponibles et prix par location.</p>
                </div>
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={() => setForm({ ...form, architectures: [...(form.architectures || []), { ...emptyArchi }] })}
                  data-testid="btn-add-archi"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                >
                  <Plus className="w-4 h-4 mr-1" /> Ajouter
                </Button>
              </div>

              {(form.architectures || []).length === 0 ? (
                <div className="text-sm text-slate-500 text-center py-4">Aucune architecture renseignée.</div>
              ) : (
                <div className="space-y-2">
                  {form.architectures.map((a, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-white rounded-md p-2 border border-slate-200" data-testid={`archi-row-${idx}`}>
                      <div className="col-span-5 space-y-1">
                        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Type d'architecture</Label>
                        <Input
                          value={a.type}
                          placeholder="Ex. F2, F3, Duplex"
                          onChange={(e) => {
                            const next = [...form.architectures];
                            next[idx] = { ...next[idx], type: e.target.value };
                            setForm({ ...form, architectures: next });
                          }}
                          data-testid={`input-archi-type-${idx}`}
                        />
                      </div>
                      <div className="col-span-3 space-y-1">
                        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Plans dispos</Label>
                        <Input
                          type="number" min="0"
                          value={a.nb_plans}
                          onChange={(e) => {
                            const next = [...form.architectures];
                            next[idx] = { ...next[idx], nb_plans: e.target.value };
                            setForm({ ...form, architectures: next });
                          }}
                          data-testid={`input-archi-nb-${idx}`}
                        />
                      </div>
                      <div className="col-span-3 space-y-1">
                        <Label className="text-[11px] uppercase tracking-wider text-slate-500">Prix (FCFA)</Label>
                        <Input
                          type="number" min="0"
                          value={a.prix}
                          onChange={(e) => {
                            const next = [...form.architectures];
                            next[idx] = { ...next[idx], prix: e.target.value };
                            setForm({ ...form, architectures: next });
                          }}
                          data-testid={`input-archi-prix-${idx}`}
                        />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <Button
                          type="button" size="sm" variant="ghost"
                          onClick={() => setForm({ ...form, architectures: form.architectures.filter((_, i) => i !== idx) })}
                          data-testid={`btn-remove-archi-${idx}`}
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4 text-rose-600" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 col-span-2">
                <Label>Adresse</Label>
                <Input value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-maison">Enregistrer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="max-w-3xl" data-testid="dialog-history">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Historique d'occupation — {histData?.maison_nom || "…"}
            </DialogTitle>
          </DialogHeader>
          {histLoading && <div className="text-slate-500 text-sm py-6 text-center">Chargement…</div>}
          {!histLoading && histData && (
            <div className="space-y-4">
              {(histData.plans || []).length === 0 && (
                <div className="text-slate-500 text-sm py-6 text-center">Aucun plan disponible sur cette maison.</div>
              )}
              {(histData.plans || []).map((p) => (
                <div key={p.plan_id} className="rounded-lg border border-slate-200 bg-white" data-testid={`hist-plan-${p.plan_id}`}>
                  <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200 rounded-t-lg">
                    <div className="flex items-center gap-3">
                      <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 font-mono text-base px-2.5 py-0.5">
                        {p.plan_id}
                      </Badge>
                      <div>
                        <div className="text-sm font-medium text-slate-900">{p.archi_type}</div>
                        <div className="text-xs text-slate-500">{formatFCFA(p.prix)}</div>
                      </div>
                    </div>
                    {p.current ? (
                      <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50">Occupé</Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100">Libre</Badge>
                    )}
                  </div>
                  <div className="px-4 py-3 space-y-2">
                    {p.current && (
                      <div className="flex items-center gap-3 text-sm">
                        <div className="w-2 h-2 rounded-full bg-emerald-500" />
                        <div className="flex-1">
                          <span className="font-medium text-slate-900">{p.current.nom}</span>
                          <span className="text-slate-500 ml-2">— depuis {formatDate(p.current.date_entree)}</span>
                        </div>
                        <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 text-[10px]">Actuel</Badge>
                      </div>
                    )}
                    {(p.history || []).map((h) => (
                      <div key={h.id} className="flex items-center gap-3 text-sm">
                        <div className="w-2 h-2 rounded-full bg-slate-300" />
                        <div className="flex-1">
                          <span className="font-medium text-slate-700">{h.nom}</span>
                          <span className="text-slate-500 ml-2">
                            {formatDate(h.date_entree)} → {h.date_sortie ? formatDate(h.date_sortie) : "—"}
                          </span>
                        </div>
                        <Badge className="bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100 text-[10px]">Précédent</Badge>
                      </div>
                    ))}
                    {!p.current && (p.history || []).length === 0 && (
                      <div className="text-xs text-slate-400 italic">Aucune occupation enregistrée pour ce plan.</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-rose-600 hover:bg-rose-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
